import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { IBodyRow, IColumn } from '@cbgms/components/grid/Types'
import { findKeyInObject, formatEnum, formatTitle } from '@cbgms/base/utils/helpers'
import { isEnum, isSliceOfEnum } from '@cbgms/components/grid/Knock/KnockHelpers'
import isFunction from 'lodash/fp/isFunction'

interface useListTranslatorsProps {
  translationKey: string
  onColumnMap?: (columns: IColumn[], preMapColumns: IColumn[], data: any) => any
  onRowMap?: (rows: any, preMapData: any, columns: IColumn[]) => any
}

export const useListTranslators = (props: useListTranslatorsProps) => {
  const { t, i18n } = useTranslation()
  const { translationKey, onColumnMap, onRowMap } = props

  const translateKey = useCallback(
    (columnNameKey: string) => {
      interface ColumnTranslationMethod {
        translationKey: string
        renderKey: (columnName: string) => void
      }
      function getLastPartOfKey(columnName: string) {
        return (columnName && columnName.split('.').pop()) || ''
      }
      const columnTranslationMethods: ColumnTranslationMethod[] = [
        { translationKey, renderKey: columnName => columnName },
        { translationKey, renderKey: columnName => columnName.toLowerCase() },
        { translationKey: 'entities', renderKey: columnName => columnName },
        { translationKey: 'entities', renderKey: columnName => columnName.toLowerCase() },
        { translationKey: 'entities.shared', renderKey: columnName => getLastPartOfKey(columnName) },
        { translationKey: 'entities.shared', renderKey: columnName => getLastPartOfKey(columnName).toLowerCase() }
      ]

      for (const columnTranslationMethod of columnTranslationMethods) {
        const fullKey = `${columnTranslationMethod.translationKey}.${columnTranslationMethod.renderKey(columnNameKey)}`
        if (i18n.exists(fullKey)) {
          return t(fullKey)
        }
      }
      return columnNameKey
    },
    [i18n]
  )

  const translateColumns = useCallback(
    (columns: IColumn[], preMapCols: IColumn[], data: any) => {
      const missingTranslations: string[] = []
      let mappedColumns = columns.map(column => {
        if (column.static) {
          return column
        }
        const translatedName = translateKey(column.key)
        if (translatedName === column.key) {
          missingTranslations.push(`entities.${translatedName}`)
        }
        return { ...column, name: formatTitle(translatedName) }
      })

      if (missingTranslations.length > 0) {
        console.debug(`Missing list column translations:\r\n${missingTranslations.join('\r\n')}`)
      }
      if (onColumnMap) {
        mappedColumns = onColumnMap(mappedColumns, preMapCols, data)
      }
      return mappedColumns
    },
    [translateKey, onColumnMap]
  )

  const translateEnums = useCallback(
    (data: any, preMapData: any, columns: IColumn[]) => {
      let mappedData = data.map((row: IBodyRow) => ({
        ...row,
        cols: row.cols.map(col => {
          const knockType = findKeyInObject(preMapData.Structure, col.key)
          if (!knockType || isFunction(col.content) || !isEnum(knockType) || (isSliceOfEnum(knockType) && !col.content)) {
            return col
          }

          let translationKey = knockType
          if (isSliceOfEnum(knockType)) {
            translationKey = knockType.replace('SliceOfEnum', 'Enum')
          }

          const content = Array.isArray(col.content)
            ? col.content.map((context: string) => formatEnum(translationKey, context))
            : formatEnum(translationKey, col.content)

          return { ...col, content }
        })
      }))

      if (onRowMap) {
        mappedData = onRowMap(mappedData, preMapData, columns)
      }
      return mappedData
    },
    [onRowMap]
  )

  return useMemo(
    () => ({
      translateKey,
      translateEnums,
      translateColumns
    }),
    [translateKey, translateEnums, translateColumns]
  )
}
